import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Typography,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ErrorOutline,
  Warning,
  Info,
  CheckCircle,
  Close,
  Refresh,
  ReportProblem,
} from '@mui/icons-material';

export interface ContactFormError {
  type: 'validation' | 'network' | 'server' | 'rate_limit' | 'unknown';
  message: string;
  details?: string;
  field?: string;
  retryable?: boolean;
  statusCode?: number;
}

interface ContactFormErrorHandlerProps {
  error: ContactFormError | null;
  onRetry?: () => void;
  onDismiss: () => void;
  retryCount?: number;
  maxRetries?: number;
}

/**
 * Comprehensive error handler for contact form submissions
 * Provides user-friendly error messages with appropriate actions
 */
const ContactFormErrorHandler: React.FC<ContactFormErrorHandlerProps> = ({
  error,
  onRetry,
  onDismiss,
  retryCount = 0,
  maxRetries = 3,
}) => {
  if (!error) return null;

  const getErrorIcon = () => {
    switch (error.type) {
      case 'validation':
        return <Warning color="warning" />;
      case 'network':
        return <ErrorOutline color="error" />;
      case 'server':
        return <ReportProblem color="error" />;
      case 'rate_limit':
        return <Info color="info" />;
      default:
        return <ErrorOutline color="error" />;
    }
  };

  const getErrorSeverity = (): 'error' | 'warning' | 'info' | 'success' => {
    switch (error.type) {
      case 'validation':
        return 'warning';
      case 'rate_limit':
        return 'info';
      case 'network':
      case 'server':
      case 'unknown':
        return 'error';
      default:
        return 'error';
    }
  };

  const getErrorTitle = () => {
    switch (error.type) {
      case 'validation':
        return 'Please Check Your Information';
      case 'network':
        return 'Connection Problem';
      case 'server':
        return 'Server Error';
      case 'rate_limit':
        return 'Too Many Attempts';
      case 'unknown':
        return 'Something Went Wrong';
      default:
        return 'Error';
    }
  };

  const getUserFriendlyMessage = () => {
    switch (error.type) {
      case 'validation':
        return 'There are some issues with the information you provided. Please review and correct the highlighted fields.';
      case 'network':
        return 'We\'re having trouble connecting to our servers. Please check your internet connection and try again.';
      case 'server':
        return 'Our servers are experiencing issues. We\'ve been notified and are working to fix this. Please try again in a few minutes.';
      case 'rate_limit':
        return 'You\'ve submitted the form multiple times recently. Please wait a moment before trying again.';
      case 'unknown':
        return 'An unexpected error occurred. Please try again or contact us directly if the problem persists.';
      default:
        return error.message;
    }
  };

  const shouldShowRetry = () => {
    return error.retryable !== false && 
           onRetry && 
           retryCount < maxRetries && 
           error.type !== 'validation' && 
           error.type !== 'rate_limit';
  };

  const getRetryText = () => {
    if (retryCount === 0) return 'Try Again';
    return `Retry (${retryCount}/${maxRetries})`;
  };

  const getAlternativeActions = () => {
    const actions = [];

    // Email contact for server errors
    if (error.type === 'server' || error.type === 'unknown') {
      actions.push(
        <Button
          key="email"
          variant="outlined"
          size="small"
          href="mailto:hello@vegadigital.com?subject=Contact Form Issue"
          sx={{ mr: 1 }}
        >
          Email Us Instead
        </Button>
      );
    }

    // Phone contact for urgent issues
    if (error.type === 'server' || error.type === 'network') {
      actions.push(
        <Button
          key="phone"
          variant="outlined"
          size="small"
          href="tel:+15551234567"
          sx={{ mr: 1 }}
        >
          Call Us
        </Button>
      );
    }

    return actions;
  };

  return (
    <Collapse in={Boolean(error)} timeout={300}>
      <Alert
        severity={getErrorSeverity()}
        icon={getErrorIcon()}
        data-testid="contact-form-error"
        sx={{
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onDismiss}
            data-testid="error-dismiss-button"
          >
            <Close fontSize="inherit" />
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 600 }}>
          {getErrorTitle()}
        </AlertTitle>
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          {getUserFriendlyMessage()}
        </Typography>

        {/* Technical details for debugging (collapsed) */}
        {error.details && (
          <Box sx={{ mt: 1 }}>
            <details>
              <summary style={{ cursor: 'pointer', fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)' }}>
                Technical Details
              </summary>
              <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {error.details}
                </Typography>
              </Box>
            </details>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {shouldShowRetry() && (
            <Button
              variant="contained"
              size="small"
              onClick={onRetry}
              startIcon={<Refresh />}
              data-testid="error-retry-button"
              disabled={retryCount >= maxRetries}
            >
              {getRetryText()}
            </Button>
          )}
          
          {getAlternativeActions()}
        </Box>

        {/* Rate limit specific message */}
        {error.type === 'rate_limit' && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
            Rate limiting helps protect against spam. Thank you for your understanding.
          </Typography>
        )}

        {/* Max retries reached message */}
        {retryCount >= maxRetries && error.retryable !== false && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'error.main' }}>
            Maximum retry attempts reached. Please try again later or contact us directly.
          </Typography>
        )}
      </Alert>
    </Collapse>
  );
};

export default ContactFormErrorHandler;

/**
 * Utility function to create standardized error objects
 */
export const createContactFormError = (
  type: ContactFormError['type'],
  message: string,
  options: Partial<ContactFormError> = {}
): ContactFormError => ({
  type,
  message,
  retryable: type !== 'validation' && type !== 'rate_limit',
  ...options,
});

/**
 * Parse API response errors into user-friendly format
 */
export const parseApiError = (error: any): ContactFormError => {
  // Network errors
  if (!error.response) {
    return createContactFormError('network', 'Network connection failed', {
      details: error.message || 'No response from server',
      retryable: true,
    });
  }

  const { status, data } = error.response;

  // Rate limiting
  if (status === 429) {
    return createContactFormError('rate_limit', 'Too many requests', {
      details: data?.message || 'Rate limit exceeded',
      retryable: false,
      statusCode: status,
    });
  }

  // Validation errors
  if (status === 422) {
    const validationMessage = data?.message || 'Validation failed';
    const errors = data?.errors ? JSON.stringify(data.errors, null, 2) : '';
    
    return createContactFormError('validation', validationMessage, {
      details: errors,
      retryable: false,
      statusCode: status,
    });
  }

  // Server errors
  if (status >= 500) {
    return createContactFormError('server', 'Server error occurred', {
      details: `HTTP ${status}: ${data?.message || 'Internal server error'}`,
      retryable: true,
      statusCode: status,
    });
  }

  // Client errors
  if (status >= 400) {
    return createContactFormError('unknown', data?.message || 'Request failed', {
      details: `HTTP ${status}: ${JSON.stringify(data, null, 2)}`,
      retryable: false,
      statusCode: status,
    });
  }

  // Unknown errors
  return createContactFormError('unknown', 'An unexpected error occurred', {
    details: JSON.stringify(error, null, 2),
    retryable: true,
  });
};